import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, Pencil, X, NotebookPen } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import { usePermissions } from '../../../contexts/PermissionContext';
import useScopedClasses from '../useScopedClasses';

// Supervisors (surveillants / principals / admins) oversee the whole section:
// they may post a General note and edit/delete anyone's entries. A plain
// teacher posts only their own subject and manages only their own entries.
const SUPERVISOR_ROLES = ['Surveillant', 'Principal', 'GlobalAdmin'];

// ============================================================
// Agenda -- the homework diary (cahier de textes). Section-level:
// pick class + section + date, see that day's entries, and (if
// permitted) add/edit/delete homework per subject. Parents read
// these in the mobile app (synced separately).
// ============================================================

const emptyForm = () => ({
  agendaId: null, classSubjectId: '', subjectLabel: '',
  homework: '', lessonContent: '', dueDate: ''
});

const AgendaBoard = ({ apiCall, year, hasPermission, showSuccess, showError }) => {
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [subjects, setSubjects] = useState([]);   // top-level ClassSubjects for the class
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(null);         // null = form hidden

  const canRecord = hasPermission && hasPermission('academic', 'agenda', 'record');
  const { roles } = usePermissions();
  const isSupervisor = roles.some(r => SUPERVISOR_ROLES.includes(r));
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem('user'))?.userId ?? null; } catch { return null; }
  })();
  // A teacher may edit/delete only their own entries; supervisors, anyone's.
  const canManage = (e) => canRecord && (isSupervisor || e.CreatedBy === currentUserId);

  // Sections for the picked class
  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => {
        setSections(s.filter(x => x.IsActive));
        setSectionCode(prev => s.some(x => x.SectionCode === prev) ? prev : (s[0]?.SectionCode || ''));
      }).catch(() => setSections([]));
  }, [apiCall, year, className]);

  // Subjects for the class (top-level only) -- feeds the subject dropdown
  useEffect(() => {
    if (!className) { setSubjects([]); return; }
    apiCall(`/academic/grading/class-subjects?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(list => setSubjects(list.filter(s => !s.ParentClassSubjectID)))
      .catch(() => setSubjects([]));
  }, [apiCall, year, className]);

  const loadEntries = useCallback(async () => {
    if (!className || !sectionCode || !date) { setEntries([]); return; }
    try {
      const data = await apiCall(
        `/academic/agenda?academicYear=${year}&className=${encodeURIComponent(className)}&sectionCode=${encodeURIComponent(sectionCode)}&from=${date}&to=${date}`);
      setEntries(data);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, sectionCode, date, showError]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const startAdd = () => setForm({
    ...emptyForm(),
    // Teachers default to (and are limited to) their own subject; supervisors
    // default to a General note.
    classSubjectId: (!isSupervisor && subjects.length > 0) ? String(subjects[0].ClassSubjectID) : ''
  });
  const startEdit = (e) => setForm({
    agendaId: e.AgendaID,
    classSubjectId: e.ClassSubjectID || '',
    subjectLabel: e.ClassSubjectID ? '' : (e.SubjectLabel || ''),
    homework: e.Homework || '',
    lessonContent: e.LessonContent || '',
    dueDate: e.DueDate ? e.DueDate.slice(0, 10) : ''
  });

  const save = async () => {
    if (!form.homework.trim()) { showError('Homework text is required'); return; }
    // Snapshot the chosen subject's name for the label (survives edits/mobile sync)
    const picked = subjects.find(s => String(s.ClassSubjectID) === String(form.classSubjectId));
    const subjectLabel = form.classSubjectId
      ? (picked?.SubjectName || null)
      : (form.subjectLabel.trim() || null);
    const body = {
      academicYear: year, className, sectionCode, entryDate: date,
      dueDate: form.dueDate || null,
      classSubjectId: form.classSubjectId ? Number(form.classSubjectId) : null,
      subjectLabel,
      homework: form.homework.trim(),
      lessonContent: form.lessonContent.trim() || null
    };
    try {
      const r = form.agendaId
        ? await apiCall(`/academic/agenda/${form.agendaId}`, { method: 'PUT', body: JSON.stringify(body) })
        : await apiCall('/academic/agenda', { method: 'POST', body: JSON.stringify(body) });
      showSuccess(r.message);
      setForm(null);
      loadEntries();
    } catch (err) { showError(err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this agenda entry?')) return;
    try {
      const r = await apiCall(`/academic/agenda/${id}`, { method: 'DELETE' });
      showSuccess(r.message);
      loadEntries();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="space-y-4">
      {/* Picker bar */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          {visibleClasses.length === 0 && <option value="">No classes</option>}
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          {sections.length === 0 && <option value="">No sections</option>}
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto" />
        <span className="text-sm text-gray-500">{entries.length} entrie(s)</span>
        <div className="hidden sm:block flex-1" />
        <Gate permission="academic.agenda.record" label="Add Agenda Entry">
          <button
            onClick={startAdd} disabled={!sectionCode || !!form}
            className="w-full sm:w-auto justify-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Plus size={15} /> Add homework
          </button>
        </Gate>
      </div>

      {/* Add / edit form */}
      {form && (
        <div className="bg-white rounded-lg shadow p-4 space-y-3 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-800 flex items-center gap-2">
              <NotebookPen size={16} /> {form.agendaId ? 'Edit entry' : 'New entry'} — {className} {sectionCode} · {date}
            </h3>
            <button onClick={() => setForm(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-xs text-gray-500 mb-1">Subject</label>
              <select
                value={form.classSubjectId}
                onChange={e => setForm(f => ({ ...f, classSubjectId: e.target.value }))}
                className="border rounded-lg px-3 py-2 text-sm w-full sm:min-w-[200px]"
              >
                {isSupervisor && <option value="">— General / other —</option>}
                {subjects.map(s => (
                  <option key={s.ClassSubjectID} value={s.ClassSubjectID}>{s.SubjectName}</option>
                ))}
              </select>
            </div>
            {!form.classSubjectId && (
              <div className="flex flex-col w-full sm:w-auto">
                <label className="text-xs text-gray-500 mb-1">Label (optional)</label>
                <input
                  type="text" value={form.subjectLabel}
                  onChange={e => setForm(f => ({ ...f, subjectLabel: e.target.value }))}
                  placeholder="e.g. Général"
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                />
              </div>
            )}
            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-xs text-gray-500 mb-1">Due date (optional)</label>
              <input
                type="date" value={form.dueDate}
                onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                className="border rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Homework</label>
            <textarea
              value={form.homework}
              onChange={e => setForm(f => ({ ...f, homework: e.target.value }))}
              rows={2} placeholder="What is assigned…"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Lesson content (optional)</label>
            <textarea
              value={form.lessonContent}
              onChange={e => setForm(f => ({ ...f, lessonContent: e.target.value }))}
              rows={2} placeholder="What was covered in class…"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end">
            <button onClick={save} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
              <Save size={15} /> {form.agendaId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Entries list */}
      <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
        {entries.map(e => (
          <div key={e.AgendaID} className="p-4 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold">
                  {e.SubjectLabel || 'Général'}
                </span>
                {e.DueDate && (
                  <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                    due {e.DueDate.slice(0, 10)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{e.Homework}</p>
              {e.LessonContent && (
                <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap"><span className="font-medium">Lesson:</span> {e.LessonContent}</p>
              )}
              <p className="text-[11px] text-gray-400 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                {e.CreatedByName && <span>by {e.CreatedByName}</span>}
                {e.UpdatedByName && <span>· edited by {e.UpdatedByName}</span>}
                {e.NotifiedAt
                  ? <span className="text-green-600">· sent {new Date(e.NotifiedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  : <span className="text-amber-600">· not sent yet</span>}
              </p>
            </div>
            {canManage(e) && (
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => startEdit(e)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit"><Pencil size={15} /></button>
                <button onClick={() => remove(e.AgendaID)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        ))}
        {entries.length === 0 && (
          <div className="text-center text-gray-500 py-8 text-sm">No agenda entries for this day.</div>
        )}
      </div>
    </div>
  );
};

export default AgendaBoard;
